import React, { useMemo, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Footer, Button, ErrorBanner
} from '../realestate/reModalShared';

import { DateField } from '../shared/DateField';

export interface CloEquityPositionLite {
  securityId: number;
  cusip?: string;
  dealName?: string;
  trancheName?: string;
  /** Live held face (fact_positions.quantity) — the partial-dispose ceiling. */
  quantity?: number;
  investmentAmount?: number;
  marketValue?: number;
  portfolioName?: string;
}

interface Props {
  position: CloEquityPositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * NAV / mark-to-market refresh for a posted CLO Equity position.
 * Posts to {@code POST /api/credit/clo-equity/{securityId}/mtm} which emits
 * an MTM trade through the CLOPostingSpi attrs-only path so
 * fact_positions.market_value is refreshed without touching quantity.
 */
const CloEquityMtmModal: React.FC<Props> = ({ position, onClose, onSuccess }) => {
  const [valuationDate, setValuationDate] = useState<string>(getTenantToday());
  const [newNav, setNewNav] = useState<string>(String(position.marketValue ?? position.investmentAmount ?? ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navDelta = useMemo(() => {
    const n = Number(newNav) || 0;
    const prev = position.marketValue ?? position.investmentAmount ?? 0;
    return n - prev;
  }, [newNav, position.marketValue, position.investmentAmount]);

  const submit = async () => {
    setError(null);
    if (!newNav || Number(newNav) <= 0) {
      setError('NAV must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/api/credit/clo-equity/${position.securityId}/mtm`, {
        newNav: Number(newNav),
        valuationDate,
        idempotencyKey: `mtm-${position.securityId}-${valuationDate}`,
      });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to record NAV mark';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Mark to NAV"
      subtitle={`${position.dealName || 'CLO Equity'}${position.trancheName ? ' • ' + position.trancheName : ''}${position.portfolioName ? ' • ' + position.portfolioName : ''}`}
      icon={<TrendingUp size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(139, 92, 246, 0.06)',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#c4b5fd'
        }}>
          This emits an <b>MTM trade</b> through the posting engine and
          refreshes <code>fact_positions.market_value</code> for this CLO Equity
          tranche. Quantity is unchanged; only the mark is updated.
        </div>

        <FieldRow>
          <Field>
            <Label>Valuation Date</Label>
            <DateField value={valuationDate ?? ''} onChange={iso => setValuationDate(iso)} />
          </Field>
          <Field>
            <Label>New NAV (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={newNav}
              onChange={e => setNewNav(e.target.value)}
            />
          </Field>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Investment</Label>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600 }}>
              ${(position.investmentAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
          <Field>
            <Label>Last MV</Label>
            <div style={{ color: '#60a5fa', fontSize: '0.95rem', fontWeight: 600 }}>
              ${(position.marketValue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
          <Field>
            <Label>Δ vs Last MV</Label>
            <div style={{
              color: navDelta >= 0 ? '#34d399' : '#f87171',
              fontSize: '0.95rem',
              fontWeight: 700
            }}>
              {navDelta >= 0 ? '+' : ''}${navDelta.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>

      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Posting…' : 'Confirm Mark'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export default CloEquityMtmModal;
